import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Route, Switch, useParams } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import { useHasPermission } from "src/utils/permissions";
import useFlash from "go-alert/AlertMessage";
import Page404 from "go-app/components/Page404/Page404";
import {
	CustomFieldContext,
	CustomFieldContextProps,
	CustomFieldService,
} from "go-component/components/CustomField/services/context";
import { CustomFieldTypeApi } from "go-component/components/CustomField/services/types";
import { CsvContext } from "go-component/components/ImportCsv/services/context";
import { JobContext, JobService } from "go-component/components/Job/services/context";
import { MessageEventContext, MessageEventService } from "go-component/components/MessageEvent/services/context";
import { MessageResourceType } from "go-component/components/MessageEvent/services/types";
import { useMomentLocaleForLocation } from "go-core/hooks/useMomentLocale";
import OrganizationIndexSecurityComponent from "go-security/components/Organization/Index";
import SecurityOrganizationAcceptTerms from "go-security/components/OrganizationAcceptTerms/OrganizationAcceptTerms";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentContext, SegmentListService } from "go-segment/context";
import { apiOrganizationComponent } from "../../../../services/Api/Organization/apiOrganizationComponent";
import { apiOrganizationSegment } from "../../../../services/Api/Organization/apiOrganizationSegment";
import { ConfigContext } from "../../../../services/Config/context";
import { getApiURL } from "../../../../utils/envutil";
import TransactionContextProvider from "./components/Transaction/Context/TransactionContext";
import { OrganizationAppsPage } from "./pages/Apps";
import OrganizationContactPage from "./pages/Contact";
import OrganizationDashboardPage from "./pages/Dashboard";
import { OrganizationDiagnosticsPage } from "./pages/Diagnostics";
import OrganizationOrderPage from "./pages/Document";
import OrganizationInstallPage from "./pages/Install";
import OrganizationLogsPage from "./pages/Logs";
import OrganizationProductPage from "./pages/Product";
import OrganizationReportsPage from "./pages/Reports";
import OrganizationSettingsPage from "./pages/Settings";
import OrganizationStockStatePage from "./pages/StockState";
import OrganizationStocktakingPage from "./pages/Stocktaking";

interface MatchParams {
	organizationId: string;
}

const OrganizationPage: React.FC<RouteComponentProps> = (props) => {
	const currentOrganization = useSelector(selectOrganization);
	const { organizationId } = useParams<MatchParams>();
	const configService = useContext(ConfigContext);
	const { t } = useTranslation();
	const baseUrl = getApiURL();
	const {
		hasStockStatePermission,
		hasDocumentPermission,
		hasStockTakingPermission,
		hasProductPermission,
		hasContactPermission,
		hasReportPermission,
		hasSupportPermission,
	} = useHasPermission();
	const nameTranslationPrefix = "enums.jobs.name";
	const { addFlash } = useFlash();

	const segmentPath = `/${currentOrganization.id}/settings/segments`;
	const typesTranslationPrefix = "enums.message_events.type";

	const resources = [
		{
			id: "CUSTOM_FIELD",
			title: t("enums.message_events.resource_type.CUSTOM_FIELD"),
			link: `/${currentOrganization.id}/settings/custom_fields/`,
		},
		{
			id: "SEGMENT",
			title: t("enums.message_events.resource_type.SEGMENT"),
			link: `/${currentOrganization.id}/settings/segments/`,
		},
		{
			id: "TAG",
			title: t("enums.message_events.resource_type.TAG"),
		},
		{
			id: "WEBHOOK",
			title: t("enums.message_events.resource_type.WEBHOOK"),
			link: `/${currentOrganization.id}/settings/`,
		},
		{
			id: "IMAGE",
			title: t("enums.message_events.resource_type.IMAGE"),
		},
		{
			id: "REQUEST",
			title: t("enums.message_events.resource_type.REQUEST"),
			link: `/${currentOrganization.id}/logs/request_items/`,
		},
		{
			id: "CONTACT",
			title: t("enums.message_events.resource_type.CONTACT"),
			link: `/${currentOrganization.id}/contacts/`,
		},
		{
			id: "PRODUCT",
			title: t("enums.message_events.resource_type.PRODUCT"),
			link: `/${currentOrganization.id}/products/`,
			linkAdditionalPath: "/edit",
		},
		{
			id: "ARCHIVE",
			title: t("enums.message_events.resource_type.ARCHIVE"),
			link: `/${currentOrganization.id}/settings/archives/`,
		},
		{
			id: "ARCHIVE_STOCK_PRODUCT",
			title: t("enums.message_events.resource_type.ARCHIVE_STOCK_PRODUCT"),
		},
		{
			id: "PRODUCT_CATEGORY",
			title: t("enums.message_events.resource_type.PRODUCT_CATEGORY"),
			link: `/${currentOrganization.id}/products/categories`,
			linkAdditionalPath: "/edit",
		},
		{
			id: "TAX",
			title: t("enums.message_events.resource_type.TAX"),
		},
		{
			id: "ROLE",
			title: t("enums.message_events.resource_type.ROLE"),
		},
		{
			id: "JOB",
			title: t("enums.message_events.resource_type.JOB"),
		},
		{
			id: "PERMISSION",
			title: t("enums.message_events.resource_type.PERMISSION"),
		},
		{
			id: "STOCK",
			title: t("enums.message_events.resource_type.STOCK"),
			link: `/${currentOrganization.id}/settings/stocks`,
			linkAdditionalPath: "/edit",
		},
		{
			id: "STOCKTAKING",
			title: t("enums.message_events.resource_type.STOCKTAKING"),
			link: `/${currentOrganization.id}/stocktakings/`,
		},
		{
			id: "STOCKTAKING_DRAFT",
			title: t("enums.message_events.resource_type.STOCKTAKING_DRAFT"),
			link: `/${currentOrganization.id}/stocktakings/`,
			linkAdditionalPath: "/drafts",
			customEntityIdFromResource: "STOCKTAKING",
		},
		{
			id: "USER",
			title: t("enums.message_events.resource_type.USER"),
		},
		{
			id: "INVOICE",
			title: t("enums.message_events.resource_type.INVOICE"),
			link: `/${currentOrganization.id}/settings/invoice`,
		},
		{
			id: "INVOICE_DETAILS",
			title: t("enums.message_events.resource_type.INVOICE_DETAILS"),
			link: `/${currentOrganization.id}/settings/invoice`,
		},
		{
			id: "INTEGRATION_CHANNEL",
			title: t("enums.message_events.resource_type.INTEGRATION_CHANNEL"),
			link: `/${currentOrganization.id}/apps/all/`,
		},
		{
			id: "WAREHOUSE",
			title: t("enums.message_events.resource_type.WAREHOUSE"),
		},
		{
			id: "PURCHASE_ORDER",
			title: t("enums.message_events.resource_type.PURCHASE_ORDER"),
			link: `/${currentOrganization.id}/documents/purchases/`,
		},
		{
			id: "PURCHASE_RECEIVE",
			title: t("enums.message_events.resource_type.PURCHASE_RECEIVE"),
			link: `/${currentOrganization.id}/documents/purchases/`,
			customEntityIdFromResource: "PURCHASE_ORDER",
		},
		{
			id: "PURCHASE_CORRECTION",
			title: t("enums.message_events.resource_type.PURCHASE_CORRECTION"),
			link: `/${currentOrganization.id}/documents/purchases/`,
			customEntityIdFromResource: "PURCHASE_ORDER",
		},
		{
			id: "SALES_ORDER",
			title: t("enums.message_events.resource_type.SALES_ORDER"),
			link: `/${currentOrganization.id}/documents/sales/`,
		},
		{
			id: "SALES_SHIP",
			title: t("enums.message_events.resource_type.SALES_SHIP"),
			link: `/${currentOrganization.id}/documents/sales`,
			customEntityIdFromResource: "SALES_ORDER",
		},
		{
			id: "SALES_RETURN",
			title: t("enums.message_events.resource_type.SALES_RETURN"),
			link: `/${currentOrganization.id}/documents/sales`,
			customEntityIdFromResource: "SALES_ORDER",
		},
		{
			id: "STOCK_ADJUSTMENT",
			title: t("enums.message_events.resource_type.STOCK_ADJUSTMENT"),
			link: `/${currentOrganization.id}/documents/stock_adjustments/`,
		},
		{
			id: "TRANSFER_ORDER",
			title: t("enums.message_events.resource_type.TRANSFER_ORDER"),
			link: `/${currentOrganization.id}/documents/transfers/`,
		},
		{
			id: "BUNDLE",
			title: t("enums.message_events.resource_type.BUNDLE"),
			link: `/${currentOrganization.id}/documents/bundles/`,
		},
		{
			id: "COMPANY",
			title: t("enums.message_events.resource_type.COMPANY"),
			link: `/${currentOrganization.id}/settings/company/`,
		},
		{
			id: "BANK_ACCOUNT",
			title: t("enums.message_events.resource_type.BANK_ACCOUNT"),
			link: `/${currentOrganization.id}/settings/company/`,
		},
		{
			id: "INTEGRATION_LINKED",
			title: t("enums.message_events.resource_type.INTEGRATION_LINKED"),
			link: `/${currentOrganization.id}/settings/apps/linking`,
		},
	] as MessageResourceType[];

	const customFieldResources = [
		{
			id: "CONTACT",
			title: t(`enums.custom_fields.resources.CONTACT`),
		},
		{
			id: "SALES_ORDER",
			title: t(`enums.custom_fields.resources.SALES_ORDER`),
		},
		{
			id: "INVOICE",
			title: t(`enums.custom_fields.resources.INVOICE`),
		},
		{
			id: "PRODUCT",
			title: t(`enums.custom_fields.resources.PRODUCT`),
		},
		{
			id: "PRODUCT_CATEGORY",
			title: t(`enums.custom_fields.resources.PRODUCT_CATEGORY`),
		},
		{
			id: "PURCHASE_ORDER",
			title: t(`enums.custom_fields.resources.PURCHASE_ORDER`),
		},
		{
			id: "TRANSFER_ORDER",
			title: t(`enums.custom_fields.resources.TRANSFER_ORDER`),
		},
		{
			id: "STOCK_ADJUSTMENT",
			title: t(`enums.custom_fields.resources.STOCK_ADJUSTMENT`),
		},
		{
			id: "PURCHASE_CORRECTION",
			title: t(`enums.custom_fields.resources.PURCHASE_CORRECTION`),
		},
		{
			id: "SALES_RETURN",
			title: t(`enums.custom_fields.resources.SALES_RETURN`),
		},
		{
			id: "BUNDLE",
			title: t(`enums.custom_fields.resources.BUNDLE`),
		},
		{
			id: "STOCKTAKING",
			title: t(`enums.custom_fields.resources.STOCKTAKING`),
		},
	] as CustomFieldTypeApi[];

	const {
		importCsv,
		finishImportCsv,
		getCsvExampleSimpleFile,
		getCsvExampleFile,
		getCsvFromVenue,
		getCsvAttributes,
		importAsyncCsv,
		finishImportAsyncCsv,
		getFinishImportCsvProgress,
		getImportCsvProgress,
	} = apiOrganizationComponent;

	const csvContextValue = {
		importCsv,
		finishImportCsv,
		getCsvExampleSimpleFile,
		getCsvExampleFile,
		getCsvFromVenue,
		getCsvAttributes,
		importAsyncCsv,
		finishImportAsyncCsv,
		getFinishImportCsvProgress,
		getImportCsvProgress,
	};

	useMomentLocaleForLocation(selectOrganization);

	return (
		<SegmentContext.Provider
			value={
				new SegmentListService(
					{
						saveSegment: apiOrganizationSegment.saveSegment,
						removeSegment: apiOrganizationSegment.removeSegment,
						getSegments: apiOrganizationSegment.getSegments,
						restoreSegment: apiOrganizationSegment.restoreSegment,
						updateSegment: apiOrganizationSegment.updateSegment,
						setAsDefault: apiOrganizationSegment.setSegmentAsDefault,
						updateSegments: apiOrganizationSegment.updateSegments,
						setNotDefault: apiOrganizationSegment.setSegmentNotDefault,
					},
					segmentPath,
					apiOrganizationSegment.getListInfo,
					addFlash
				)
			}
		>
			<MessageEventContext.Provider
				value={
					new MessageEventService(
						{
							all: apiOrganizationComponent.getMessageEvents,
							get: apiOrganizationComponent.getMessageEvent,
							getTypeOptions: apiOrganizationComponent.getMessageEventTypes,
						},
						resources,
						typesTranslationPrefix,
						t
					)
				}
			>
				<CustomFieldContext.Provider
					value={
						new CustomFieldService(
							{
								all: apiOrganizationComponent.getCustomFields,
								get: apiOrganizationComponent.getCustomField,
								create: apiOrganizationComponent.createCustomField,
								update: apiOrganizationComponent.updateCustomField,
								remove: apiOrganizationComponent.removeCustomField,
								restore: apiOrganizationComponent.restoreCustomField,
							} as CustomFieldContextProps,
							customFieldResources
						)
					}
				>
					<JobContext.Provider
						value={
							new JobService(
								{
									all: apiOrganizationComponent.getJobs,
									get: apiOrganizationComponent.getJob,
									cancel: apiOrganizationComponent.cancelJob,
									getNameOptions: apiOrganizationComponent.getJobNames,
								},
								nameTranslationPrefix,
								t
							)
						}
					>
						<CsvContext.Provider value={csvContextValue}>
							<TransactionContextProvider>
								<OrganizationIndexSecurityComponent
									organizationId={organizationId}
									locationState={props.location.state}
									goaccounts_url={configService.getGoAccountsUrl()}
									baseUrl={baseUrl}
								>
									<Switch>
										<Route
											path={`${props.match.url}/install`}
											component={OrganizationInstallPage}
										/>
										<Route
											path={`${props.match.url}/settings`}
											component={OrganizationSettingsPage}
										/>
										{hasContactPermission && (
											<Route
												path={`${props.match.url}/contacts`}
												component={OrganizationContactPage}
											/>
										)}
										{hasProductPermission && (
											<Route
												path={`${props.match.url}/products`}
												component={OrganizationProductPage}
											/>
										)}
										{hasDocumentPermission && (
											<Route
												path={`${props.match.url}/documents`}
												component={OrganizationOrderPage}
											/>
										)}
										{hasReportPermission && (
											<Route
												path={`${props.match.url}/reports`}
												component={OrganizationReportsPage}
											/>
										)}
										<Route path={`${props.match.url}/logs`} component={OrganizationLogsPage} />
										<Route path={`${props.match.url}/apps`} component={OrganizationAppsPage} />
										{hasStockTakingPermission && (
											<Route
												path={`${props.match.url}/stocktakings`}
												component={OrganizationStocktakingPage}
											/>
										)}
										{hasStockStatePermission && (
											<Route
												path={`${props.match.url}/states`}
												component={OrganizationStockStatePage}
											/>
										)}
										<Route exact path={"/:id"} component={OrganizationDashboardPage} />
										<Route
											exact
											path={"/:id/accept_terms"}
											component={SecurityOrganizationAcceptTerms}
										/>
										{hasSupportPermission && (
											<Route
												path={`${props.match.url}/diagnostics`}
												component={OrganizationDiagnosticsPage}
											/>
										)}
										<Route component={Page404} />
									</Switch>
								</OrganizationIndexSecurityComponent>
							</TransactionContextProvider>
						</CsvContext.Provider>
					</JobContext.Provider>
				</CustomFieldContext.Provider>
			</MessageEventContext.Provider>
		</SegmentContext.Provider>
	);
};
export default OrganizationPage;

// t("enums.jobs.name.CALCULATE");
// t("enums.jobs.name.IMPORT");
// t("enums.jobs.name.EXPORT");
// t("enums.jobs.name.EXPORT_ORDER_COST");
// t("enums.jobs.name.IMPORT_CSV");

// t("enums.message_events.type.WEBHOOK_CREATED");
// t("enums.message_events.type.WEBHOOK_UPDATED");
// t("enums.message_events.type.WEBHOOK_DELETED");
// t("enums.message_events.type.WEBHOOK_RESTORED");
// t("enums.message_events.type.SEGMENT_CREATED");
// t("enums.message_events.type.SEGMENT_UPDATED");
// t("enums.message_events.type.SEGMENT_DELETED");
// t("enums.message_events.type.SEGMENT_DELETED_PERMANENTLY");
// t("enums.message_events.type.SEGMENT_RESTORED");
// t("enums.message_events.type.IMAGE_CREATED");
// t("enums.message_events.type.IMAGE_DELETED");
// t("enums.message_events.type.IMAGE_COPIED");
// t("enums.message_events.type.CUSTOM_FIELD_CREATED");
// t("enums.message_events.type.CUSTOM_FIELD_UPDATED");
// t("enums.message_events.type.CUSTOM_FIELD_RESTORED");
// t("enums.message_events.type.CUSTOM_FIELD_DELETED");
// t("enums.message_events.type.CUSTOM_FIELD_DELETED_PERMANENTLY");
// t("enums.message_events.type.TAG_CREATED");
// t("enums.message_events.type.TAG_UPDATED");
// t("enums.message_events.type.TAG_DELETED");
// t("enums.message_events.type.PO_CORRECTION");
// t("enums.message_events.type.ST_SET_STATE");
// t("enums.message_events.type.SALES_ORDER_LOCKED");
// t("enums.message_events.type.SALES_ORDER_UNLOCKED");
// t("enums.message_events.type.STOCKTAKING_DRAFT_PRODUCT_STATE_UPDATED");
// t("enums.message_events.type.STOCKTAKING_DRAFT_PRODUCT_STATE_CREATED");
// t("enums.message_events.type.STOCKTAKING_DRAFT_PRODUCT_STATE_DELETED");
// t("enums.message_events.type.ARCHIVE_CREATED");
// t("enums.message_events.type.ARCHIVE_UPDATED");
// t("enums.message_events.type.ARCHIVE_CLOSED");
// t("enums.message_events.type.ARCHIVE_STOCK_PRODUCT_UPDATED");
// t("enums.message_events.type.PRODUCT_UPDATED");
// t("enums.message_events.type.PRODUCT_CREATED");
// t("enums.message_events.type.PRODUCT_COMPONENTS_CORRECTED");
// t("enums.message_events.type.PRODUCT_STATE_CORRECTED");
// t("enums.message_events.type.PRODUCT_MERGED");
// t("enums.message_events.type.PRODUCT_DELETED");
// t("enums.message_events.type.PRODUCT_RESTORED");
// t("enums.message_events.type.PRODUCT_CATEGORY_UPDATED");
// t("enums.message_events.type.PRODUCT_CATEGORY_CREATED");
// t("enums.message_events.type.PRODUCT_CATEGORY_RESTORED");
// t("enums.message_events.type.PRODUCT_CATEGORY_DELETED");
// t("enums.message_events.type.CONTACT_UPDATED");
// t("enums.message_events.type.CONTACT_CREATED");
// t("enums.message_events.type.CONTACT_DELETED");
// t("enums.message_events.type.CONTACT_RESTORED");
// t("enums.message_events.type.JOB_RESUMED");
// t("enums.message_events.type.JOB_CANCELED");
// t("enums.message_events.type.TAX_CREATED");
// t("enums.message_events.type.WAREHOUSE_UPDATED");
// t("enums.message_events.type.PURCHASE_ORDER_UPDATED");
// t("enums.message_events.type.PURCHASE_ORDER_CREATED");
// t("enums.message_events.type.PURCHASE_ORDER_RESTORED");
// t("enums.message_events.type.PURCHASE_ORDER_CANCELED");
// t("enums.message_events.type.PURCHASE_ORDER_VOIDED");
// t("enums.message_events.type.PURCHASE_ORDER_REVOKED");
// t("enums.message_events.type.PURCHASE_ORDER_DELETED");
// t("enums.message_events.type.PURCHASE_ORDER_CONFIRMED");
// t("enums.message_events.type.PURCHASE_ORDER_CLOSED");
// t("enums.message_events.type.PURCHASE_ORDER_CLOSE_CANCELED");
// t("enums.message_events.type.PURCHASE_ORDER_LOCKED");
// t("enums.message_events.type.PURCHASE_ORDER_UNLOCKED");
// t("enums.message_events.type.PURCHASE_RECEIVE_CREATED");
// t("enums.message_events.type.PURCHASE_RECEIVE_DELETED");
// t("enums.message_events.type.PURCHASE_CORRECTION_CREATED");
// t("enums.message_events.type.PURCHASE_CORRECTION_UPDATED");
// t("enums.message_events.type.PURCHASE_CORRECTION_VOIDED");
// t("enums.message_events.type.PURCHASE_CORRECTION_RESTORED");
// t("enums.message_events.type.PURCHASE_CORRECTION_CLOSED");
// t("enums.message_events.type.PURCHASE_CORRECTION_CLOSE_CANCELED");
// t("enums.message_events.type.PURCHASE_CORRECTION_DELETED");
// t("enums.message_events.type.SALES_ORDER_UPDATED");
// t("enums.message_events.type.SALES_ORDER_REVOKED");
// t("enums.message_events.type.SALES_ORDER_CREATED");
// t("enums.message_events.type.SALES_ORDER_CANCELED");
// t("enums.message_events.type.SALES_ORDER_DELETED");
// t("enums.message_events.type.SALES_ORDER_VOIDED");
// t("enums.message_events.type.SALES_ORDER_CLOSED");
// t("enums.message_events.type.SALES_ORDER_CONFIRMED");
// t("enums.message_events.type.SALES_ORDER_RESTORED");
// t("enums.message_events.type.SALES_RETURN_CREATED");
// t("enums.message_events.type.SALES_RETURN_UPDATED");
// t("enums.message_events.type.SALES_RETURN_VOIDED");
// t("enums.message_events.type.SALES_RETURN_RESTORED");
// t("enums.message_events.type.SALES_RETURN_CLOSED");
// t("enums.message_events.type.SALES_RETURN_CLOSE_CANCELED");
// t("enums.message_events.type.SALES_RETURN_DELETED")
// t("enums.message_events.type.STOCK_ADJUSTMENT_UPDATED");
// t("enums.message_events.type.STOCK_ADJUSTMENT_CANCELED");
// t("enums.message_events.type.STOCK_ADJUSTMENT_CREATED");
// t("enums.message_events.type.STOCK_ADJUSTMENT_VOIDED");
// t("enums.message_events.type.STOCK_ADJUSTMENT_RESTORED");
// t("enums.message_events.type.STOCK_ADJUSTMENT_CLOSED");
// t("enums.message_events.type.STOCK_ADJUSTMENT_CLOSE_CANCELED");
// t("enums.message_events.type.STOCK_ADJUSTMENT_DELETED");
// t("enums.message_events.type.STOCK_ADJUSTMENT_CONFIRMED");
// t("enums.message_events.type.TRANSFER_ORDER_UPDATED");
// t("enums.message_events.type.TRANSFER_ORDER_CREATED");
// t("enums.message_events.type.TRANSFER_ORDER_VOIDED");
// t("enums.message_events.type.TRANSFER_ORDER_DELETED");
// t("enums.message_events.type.TRANSFER_ORDER_CANCELED");
// t("enums.message_events.type.TRANSFER_ORDER_CONFIRMED");
// t("enums.message_events.type.TRANSFER_ORDER_CLOSED");
// t("enums.message_events.type.TRANSFER_ORDER_RESTORED");
// t("enums.message_events.type.TRANSFER_ORDER_CLOSE_CANCELED");
// t("enums.message_events.type.BUNDLE_UPDATED");
// t("enums.message_events.type.BUNDLE_CANCELED");
// t("enums.message_events.type.BUNDLE_CREATED");
// t("enums.message_events.type.BUNDLE_VOIDED");
// t("enums.message_events.type.BUNDLE_RESTORED");
// t("enums.message_events.type.BUNDLE_CLOSED");
// t("enums.message_events.type.BUNDLE_CLOSE_CANCELED");
// t("enums.message_events.type.BUNDLE_DELETED");
// t("enums.message_events.type.BUNDLE_CONFIRMED");
// t("enums.message_events.type.INTEGRATION_CHANNEL_CREATED");
// t("enums.message_events.type.INTEGRATION_CHANNEL_REINSTALLED");
// t("enums.message_events.type.INTEGRATION_CHANNEL_DEFAULT_STOCK_LINKED");
// t("enums.message_events.type.INTEGRATION_CHANNEL_DEFAULT_STOCK_SKIPPED");
// t("enums.message_events.type.INTEGRATION_CHANNEL_UPDATED");
// t("enums.message_events.type.INTEGRATION_CHANNEL_ACTIVATED");
// t("enums.message_events.type.INTEGRATION_CHANNEL_ERROR");
// t("enums.message_events.type.INTEGRATION_CHANNEL_DELETED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_LINKED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_UNLINKED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_LINK_CLOSED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_LINK_CORRECTED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_SKIPPED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_DELETED");
// t("enums.message_events.type.INTEGRATION_CONTACT_LINKED");
// t("enums.message_events.type.INTEGRATION_CONTACT_UNLINKED");
// t("enums.message_events.type.INTEGRATION_CONTACT_UNLINKED");
// t("enums.message_events.type.INTEGRATION_CONTACT_SKIPPED");
// t("enums.message_events.type.INTEGRATION_CONTACT_DELETED");
// t("enums.message_events.type.INTEGRATION_STOCK_LINKED");
// t("enums.message_events.type.INTEGRATION_STOCK_UNLINKED");
// t("enums.message_events.type.INTEGRATION_STOCK_SKIPPED");
// t("enums.message_events.type.INTEGRATION_STOCK_DELETED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_CATEGORY_LINKED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_CATEGORY_SKIPPED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_CATEGORY_UNLINKED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_CATEGORY_DELETED");
// t("enums.message_events.type.STOCK_CREATED");
// t("enums.message_events.type.STOCK_UPDATED");
// t("enums.message_events.type.STOCK_DELETED");
// t("enums.message_events.type.STOCK_RESTORED");
// t("enums.message_events.type.STOCKTAKING_CREATED");
// t("enums.message_events.type.STOCKTAKING_UPDATED");
// t("enums.message_events.type.STOCKTAKING_STARTED");
// t("enums.message_events.type.STOCKTAKING_CANCELED");
// t("enums.message_events.type.STOCKTAKING_CLOSED");
// t("enums.message_events.type.STOCKTAKING_CLOSE_CANCELED");
// t("enums.message_events.type.STOCKTAKING_VOIDED");
// t("enums.message_events.type.STOCKTAKING_RESTORED");
// t("enums.message_events.type.STOCKTAKING_DELETED");
// t("enums.message_events.type.STOCKTAKING_DRAFT_CREATED");
// t("enums.message_events.type.STOCKTAKING_DRAFT_DELETED");
// t("enums.message_events.type.STOCKTAKING_DRAFT_AUTOFILL");
// t("enums.message_events.type.STOCKTAKING_DRAFT_UPDATED");
// t("enums.message_events.type.INVOICE_CREATED");
// t("enums.message_events.type.INVOICE_UPDATED");
// t("enums.message_events.type.INVOICE_DELETED");
// t("enums.message_events.type.INVOICE_CLOSED");
// t("enums.message_events.type.INVOICE_CANCELED");
// t("enums.message_events.type.INVOICE_DETAILS_UPDATED");
// t("enums.message_events.type.USER_ADDED_TO_ORGANIZATION");
// t("enums.message_events.type.USER_DELETED_FROM_ORGANIZATION");
// t("enums.message_events.type.IMPORT");
// t("enums.message_events.type.EXPORT");
// t("enums.message_events.type.EXPORT_COST");
// t("enums.message_events.type.CORRECT_LINKS");
// t("enums.message_events.type.CORRECT_COMPONENTS");
// t("enums.message_events.type.CORRECT_INCOMING_COMMITTED");
// t("enums.message_events.type.RECALCULATE");
// t("enums.message_events.type.ORGANIZATION_ROLE_CREATED");
// t("enums.message_events.type.ORGANIZATION_ROLE_UPDATED");
// t("enums.message_events.type.ORGANIZATION_ROLE_DELETED");
// t("enums.message_events.type.ARCHIVE_CREATED");
// t("enums.message_events.type.ARCHIVE_UPDATED");
// t("enums.message_events.type.ARCHIVE_CLOSED");
// t("enums.message_events.type.ARCHIVE_DELETED");
// t("enums.message_events.type.PURCHASE_ORDER_CANCELED");
// t("enums.message_events.type.PURCHASE_ORDER_VOIDED");
// t("enums.message_events.type.PURCHASE_ORDER_REVOKED");
// t("enums.message_events.type.PURCHASE_ORDER_DELETED");
// t("enums.message_events.type.PURCHASE_ORDER_CONFIRMED");
// t("enums.message_events.type.PURCHASE_ORDER_CLOSED");
// t("enums.message_events.type.PURCHASE_ORDER_CLOSE_CANCELED");
// t("enums.message_events.type.PURCHASE_ORDER_LOCKED");
// t("enums.message_events.type.PURCHASE_ORDER_RECEIVED");
// t("enums.message_events.type.PURCHASE_ORDER_UNLOCKED");
// t("enums.message_events.type.PURCHASE_RECEIVE_CREATED");
// t("enums.message_events.type.PURCHASE_RECEIVE_DELETED");
// t("enums.message_events.type.PURCHASE_ORDER_MAIL_SENT");
// t("enums.message_events.type.SALES_ORDER_REVOKED");
// t("enums.message_events.type.SALES_ORDER_CREATED");
// t("enums.message_events.type.SALES_ORDER_CANCELED");
// t("enums.message_events.type.SALES_ORDER_DELETED");
// t("enums.message_events.type.SALES_ORDER_VOIDED");
// t("enums.message_events.type.SALES_ORDER_CLOSED");
// t("enums.message_events.type.SALES_ORDER_CONFIRMED");
// t("enums.message_events.type.SALES_ORDER_RESTORED");
// t("enums.message_events.type.SALES_ORDER_LOCKED");
// t("enums.message_events.type.SALES_ORDER_UNLOCKED");
// t("enums.message_events.type.SALES_SHIP_CREATED");
// t("enums.message_events.type.SALES_SHIP_DELETED");
// t("enums.message_events.type.SALES_RETURN_CREATED");
// t("enums.message_events.type.SALES_RETURN_UPDATED");
// t("enums.message_events.type.SALES_RETURN_VOIDED");
// t("enums.message_events.type.SALES_RETURN_RESTORED");
// t("enums.message_events.type.SALES_RETURN_CLOSED");
// t("enums.message_events.type.SALES_RETURN_CLOSE_CANCELED");
// t("enums.message_events.type.SALES_RETURN_DELETED");
// t("enums.message_events.type.STOCK_ADJUSTMENT_UPDATED");
// t("enums.message_events.type.BUNDLE_DELETED");
// t("enums.message_events.type.BUNDLE_CONFIRMED");
// t("enums.message_events.type.INTEGRATION_CHANNEL_CREATED");
// t("enums.message_events.type.INTEGRATION_CHANNEL_REINSTALLED");
// t("enums.message_events.type.INTEGRATION_CHANNEL_DEFAULT_STOCK_LINKED");
// t("enums.message_events.type.INTEGRATION_CHANNEL_DEFAULT_STOCK_SKIPPED");
// t("enums.message_events.type.INTEGRATION_CHANNEL_UPDATED");
// t("enums.message_events.type.INTEGRATION_CHANNEL_ACTIVATED");
// t("enums.message_events.type.INTEGRATION_CHANNEL_RESET");
// t("enums.message_events.type.INTEGRATION_CHANNEL_ERROR");
// t("enums.message_events.type.INTEGRATION_CHANNEL_DELETED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_LINKED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_DELETED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_UNLINKED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_LINK_CLOSED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_LINK_CORRECTED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_SKIPPED");
// t("enums.message_events.type.INTEGRATION_CONTACT_LINKED");
// t("enums.message_events.type.INTEGRATION_CONTACT_DELETED");
// t("enums.message_events.type.INTEGRATION_CONTACT_UNLINKED");
// t("enums.message_events.type.INTEGRATION_CONTACT_SKIPPED");
// t("enums.message_events.type.INTEGRATION_CONTACT_LINK_CORRECTED");
// t("enums.message_events.type.INTEGRATION_STOCK_LINKED");
// t("enums.message_events.type.INTEGRATION_STOCK_DELETED");
// t("enums.message_events.type.INTEGRATION_STOCK_UNLINKED");
// t("enums.message_events.type.INTEGRATION_STOCK_SKIPPED");
// t("enums.message_events.type.INTEGRATION_STOCK_LINK_CORRECTED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_CATEGORY_LINKED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_CATEGORY_DELETED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_CATEGORY_SKIPPED");
// t("enums.message_events.type.INTEGRATION_PRODUCT_CATEGORY_UNLINKED");
// t("enums.message_events.type.STOCKTAKING_CLOSE_CANCELED");
// t("enums.message_events.type.STOCKTAKING_VOIDED");
// t("enums.message_events.type.STOCKTAKING_RESTORED");
// t("enums.message_events.type.STOCKTAKING_DELETED");
// t("enums.message_events.type.STOCKTAKING_DRAFT_CREATED");
// t("enums.message_events.type.STOCKTAKING_DRAFT_DELETED");
// t("enums.message_events.type.STOCKTAKING_DRAFT_AUTOFILL");
// t("enums.message_events.type.STOCKTAKING_DRAFT_UPDATED");
// t("enums.message_events.type.STOCKTAKING_DRAFT_PRODUCT_STATE_CREATED");
// t("enums.message_events.type.STOCKTAKING_DRAFT_PRODUCT_STATE_UPDATED");
// t("enums.message_events.type.STOCKTAKING_DRAFT_PRODUCT_STATE_DELETED");
// t("enums.message_events.type.INVOICE_CREATED");
// t("enums.message_events.type.INVOICE_UPDATED");
// t("enums.message_events.type.INVOICE_DELETED");
// t("enums.message_events.type.INVOICE_CLOSED");
// t("enums.message_events.type.INVOICE_CANCELED");
// t("enums.message_events.type.SEGMENT_RESTORED");
